import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Skeleton } from "@mui/material";
import DescriptionIcon from "@mui/icons-material/Description";
import GroupIcon from "@mui/icons-material/Group";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import GridViewIcon from "@mui/icons-material/GridView";
import TableRowsIcon from "@mui/icons-material/TableRows";
import NavHeader from "../nav/NavHeader";
import SideMenu from "../nav/Sidemenu";
import RankedTable from "../layout/RankedTable";
import { apiService } from "../../services/apiClient";
import { apiUrl } from "../../services/apiUrl";
import bannerImg from "../../assets/img/banner.png";
import "../../assets/css/reportRegistrations.css";

const DepartmentPage = () => {
  const navigate = useNavigate();
  const [personas, setPersonas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [viewMode, setViewMode] = useState("grid");

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await apiService.get(apiUrl.personasWithUsers());
      const raw = res.data?.data ?? [];
      setPersonas(raw);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filtered = personas.filter((p) =>
    p.personaName.toLowerCase().includes(searchText.toLowerCase())
  );

  const goToOverview = (groupId) => navigate(`/overview/${groupId}`);

  const totalReports = personas.reduce((sum, p) => sum + (p.reportCount || 0), 0);

  const mostRecentVisit = personas.reduce((latest, p) => {
    if (!p.lastVisitedAt) return latest;
    if (!latest || new Date(p.lastVisitedAt) > new Date(latest.lastVisitedAt)) return p;
    return latest;
  }, null);

  const lastVisitedDisplay = mostRecentVisit?.lastVisitedAt
    ? new Date(mostRecentVisit.lastVisitedAt).toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : null;

  return (
    <div className="d-flex flex-column min-vh-100" style={{ background: "#f8f9fb" }}>
      <NavHeader />
      <div className="d-flex flex-grow-1">
        <SideMenu />

        <main id="main" className="flex-grow-1" style={{ overflowY: "auto" }}>
          {/* Full-width header banner with overlaid title, subtitle, and counts */}
          <div style={{ position: "relative", width: "100%", height: 340, overflow: "hidden", borderRadius: 15 }}>
            <img
              src={bannerImg}
              alt="Department banner"
              style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
            />
            <div style={{ ...heroOverlayStyle, height: 340 }}>
              <div style={{ padding: "0 32px" }}>
                <h1 style={{ margin: 0, fontSize: 32, fontWeight: 700, color: "#fff" }}>
                  Department Overview
                </h1>
                <p style={{ margin: "6px 0 20px", fontSize: 14, color: "rgba(255,255,255,0.85)" }}>
                  User groups and their assigned users
                </p>
                {!loading && (
                  <div style={{ display: "flex", gap: 16 }}>
                    <div style={heroBadgeStyle}>
                      <GroupIcon style={{ color: "#fff", fontSize: 20 }} />
                      <span style={{ fontWeight: 700, fontSize: 20, color: "#fff" }}>
                        {personas.length}
                      </span>
                      <span style={{ fontSize: 12, color: "rgba(255,255,255,0.85)" }}>User Groups</span>
                    </div>
                    <div style={heroBadgeStyle}>
                      <DescriptionIcon style={{ color: "#fff", fontSize: 20 }} />
                      <span style={{ fontWeight: 700, fontSize: 20, color: "#fff" }}>
                        {totalReports}
                      </span>
                      <span style={{ fontSize: 12, color: "rgba(255,255,255,0.85)" }}>Total Reports</span>
                    </div>
                    {lastVisitedDisplay && (
                      <div style={heroBadgeStyle}>
                        <AccessTimeIcon style={{ color: "#fff", fontSize: 20 }} />
                        <div style={{ display: "flex", flexDirection: "column" }}>
                          <span style={{ fontSize: 12, fontWeight: 700, color: "#fff" }}>
                            {mostRecentVisit.lastVisitedReport || "Unknown report"}
                          </span>
                          <span style={{ fontSize: 11, color: "rgba(255,255,255,0.85)" }}>
                            Last visited · {lastVisitedDisplay}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="pt-4 py-6">

          {/* Search + view toggle */}
          <div className="mb-4 d-flex align-items-center justify-content-between">
            <input
              type="text"
              className="form-control rr-input"
              placeholder="Search user groups…"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ maxWidth: 320 }}
            />
            <div style={viewToggleWrapStyle}>
              <button
                type="button"
                title="Grid view"
                onClick={() => setViewMode("grid")}
                style={viewMode === "grid" ? viewToggleBtnActiveStyle : viewToggleBtnStyle}
              >
                <GridViewIcon style={{ fontSize: 18 }} />
              </button>
              <button
                type="button"
                title="Table view"
                onClick={() => setViewMode("table")}
                style={viewMode === "table" ? viewToggleBtnActiveStyle : viewToggleBtnStyle}
              >
                <TableRowsIcon style={{ fontSize: 18 }} />
              </button>
            </div>
          </div>

          {/* KPI card grid / table */}
          {loading ? (
            <div style={gridStyle}>
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <Skeleton key={i} variant="rounded" height={200} />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <p style={{ color: "#9ca3af", fontSize: 14 }}>
              {searchText ? "No user groups match your search." : "No user groups found."}
            </p>
          ) : viewMode === "grid" ? (
            <div style={gridStyle}>
              {filtered.map((p) => (
                <PersonaKPICard key={p.userGroupId} persona={p} onClick={() => goToOverview(p.userGroupId)} />
              ))}
            </div>
          ) : (
            <PersonaTable personas={filtered} onRowClick={(p) => goToOverview(p.userGroupId)} />
          )}
          </div>{/* end p-4 */}
        </main>
      </div>
    </div>
  );
};

const PersonaKPICard = ({ persona, onClick }) => {
  const createdAt = persona.createdAt
    ? new Date(persona.createdAt).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "—";

  return (
    <div style={cardStyle} onClick={onClick} role="button" tabIndex={0}>
      <div style={cardAccentBar} />

      <div style={{ padding: "16px 18px 18px" }}>
        <h6 style={{ margin: "0 0 12px", fontWeight: 700, fontSize: 15, color: "#1a1a2e" }}>
          {persona.personaName}
        </h6>

        {/* Report count KPI */}
        <div style={kpiRowStyle}>
          <DescriptionIcon style={{ fontSize: 24, color: "var(--app-icon-color)" }} />
          <span style={{ fontSize: 26, fontWeight: 700, color: "#1a1a2e", lineHeight: 1 }}>
            {persona.reportCount ?? 0}
          </span>
          <span style={{ fontSize: 11, color: "#6b7280", alignSelf: "flex-end", paddingBottom: 2 }}>
            {persona.reportCount === 1 ? "Report" : "Reports"}
          </span>
        </div>

        {/* Created by / at */}
        <div style={{ display: "flex", flexDirection: "column", gap: 4, marginTop: 12 }}>
          {persona.createdBy && (
            <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
              <PersonOutlineIcon style={{ fontSize: 12, color: "#9ca3af" }} />
              <span style={{ fontSize: 11, color: "#6b7280" }}>{persona.createdBy}</span>
            </div>
          )}
          <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
            <CalendarTodayIcon style={{ fontSize: 11, color: "#9ca3af" }} />
            <span style={{ fontSize: 11, color: "#9ca3af" }}>{createdAt}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const gridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
  gap: "18px",
};

const PersonaTable = ({ personas, onRowClick }) => {
  const formatDate = (value) =>
    value
      ? new Date(value).toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        })
      : "—";

  return (
    <RankedTable
      columns={[
        { label: "Persona Name" },
        { label: "Reports", align: "center" },
        { label: "Created By" },
        { label: "Created At" },
      ]}
      rows={personas}
      emptyText="No user groups found"
      renderRow={(p) => (
        <>
          <td
            className="px-5 py-3 font-semibold text-gray-800"
            style={{ cursor: "pointer" }}
            onClick={() => onRowClick(p)}
          >
            {p.personaName}
          </td>
          <td className="px-5 py-3 text-center" style={{ cursor: "pointer" }} onClick={() => onRowClick(p)}>
            <span className="inline-flex items-center gap-1 justify-center">
              <DescriptionIcon style={{ fontSize: 16, color: "var(--app-icon-color)" }} />
              {p.reportCount ?? 0}
            </span>
          </td>
          <td className="px-5 py-3 text-gray-600" style={{ cursor: "pointer" }} onClick={() => onRowClick(p)}>
            {p.createdBy || "—"}
          </td>
          <td className="px-5 py-3 text-gray-500" style={{ cursor: "pointer" }} onClick={() => onRowClick(p)}>
            {formatDate(p.createdAt)}
          </td>
        </>
      )}
    />
  );
};

const cardStyle = {
  background: "#fff",
  borderRadius: 12,
  boxShadow: "0 1px 4px rgba(0,0,0,0.07)",
  border: "1px solid #f0f0f0",
  overflow: "hidden",
  cursor: "pointer",
};

const cardAccentBar = {
  height: 4,
  background: "var(--app-icon-color)",
};

const kpiRowStyle = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  background: "color-mix(in srgb, var(--app-icon-color) 8%, white)",
  borderRadius: 8,
  padding: "8px 12px",
  marginBottom: 4,
};

const heroOverlayStyle = {
  position: "absolute",
  inset: 0,
  display: "flex",
  alignItems: "flex-end",
  paddingBottom: 36,
  background:
    "linear-gradient(0deg, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.3) 45%, transparent 100%), " +
    "linear-gradient(120deg, color-mix(in srgb, var(--app-icon-color) 55%, transparent) 0%, transparent 60%)",
};

const heroBadgeStyle = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  background: "rgba(255,255,255,0.15)",
  border: "1px solid rgba(255,255,255,0.3)",
  borderRadius: 10,
  padding: "8px 16px",
  backdropFilter: "blur(4px)",
};

const viewToggleWrapStyle = {
  display: "flex",
  gap: 2,
  background: "#f3f4f6",
  border: "1px solid #e5e7eb",
  borderRadius: 8,
  padding: 2,
};

const viewToggleBtnStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 32,
  height: 32,
  border: "none",
  borderRadius: 6,
  background: "transparent",
  color: "#6b7280",
  cursor: "pointer",
};

const viewToggleBtnActiveStyle = {
  ...viewToggleBtnStyle,
  background: "#fff",
  color: "var(--app-icon-color)",
  boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
};

export default DepartmentPage;
